import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { CheckCircle, Clock, Check } from 'lucide-react';
import { toast } from 'sonner';
import * as Sentry from '@sentry/react';
import { markFullStoryEvent, getFullStoryReplayUrl } from '@core/analytics/fullstory';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Private } from '@shared/components/common/Private';
import { BookingPageLayout } from '@features/booking/layouts';
import { useBooking } from '@features/booking/state';
import { useRestaurant } from '@core/tenant/TenantProvider';
import { useAuth } from '@core/auth/AuthProvider';
import { useAnalytics, ANALYTICS_EVENTS } from '@core/analytics';
import { useLanguage } from '@core/i18n';
import { saveReservationNote, saveCompanions } from '@features/booking/api/reservations';
import { CompanionInputs } from '@features/booking/components/CompanionInputs';
import { GuestDetailsCard } from '@features/booking/components/GuestDetailsCard';
import { SummaryRow } from '@features/booking/components/SummaryRow';
import { useRegularCompanions } from '@features/booking/hooks/useRegularCompanions';
import { resolvePairingName } from '@features/booking/utils/pairings';
import { localizeAreaById } from '@features/booking/utils/areaI18n';
import { toTitleCase } from '@features/booking/utils/toTitleCase';
import { classifyBookingFinalGuard } from '@features/booking/utils/bookingFinalGuard';
import { sanitizeHtml } from '@shared/utils/sanitize';
import type { CompanionData } from '@features/booking/api/reservations';
import type { RegularCompanion } from '@features/booking/api/companions';
import { PUBLIC_SEARCH_MODE } from '@shared/types/restaurant';

const OCCASIONS = [
  { value: 'birthday', labelKey: 'BUY_A_GIFTCARD_OCCASION_STRING_1' },
  { value: 'anniversary', labelKey: 'BUY_A_GIFTCARD_OCCASION_STRING_2' },
  { value: 'business', labelKey: 'BUY_A_GIFTCARD_OCCASION_STRING_3' },
  { value: 'trip', labelKey: 'BUY_A_GIFTCARD_OCCASION_STRING_4' },
  { value: 'thankyou', labelKey: 'BUY_A_GIFTCARD_OCCASION_STRING_5' },
  { value: 'gift', labelKey: 'BUY_A_GIFTCARD_OCCASION_STRING_6' },
] as const;

const SERVICE_LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'pt', label: 'Português' },
] as const;

const BookingFinalPage = () => {
  const { t } = useTranslation('booking');
  const navigate = useNavigate();
  const booking = useBooking();
  const restaurant = useRestaurant();
  const { authState } = useAuth();
  const { currentLanguage } = useLanguage();
  const { trackEvent } = useAnalytics();

  const { data: regularCompanions } = useRegularCompanions();

  const [note, setNote] = useState('');
  const [occasion, setOccasion] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [languageService, setLanguageService] = useState('');
  const [companions, setCompanions] = useState<CompanionData[]>([]);
  const [availableRegulars, setAvailableRegulars] = useState<RegularCompanion[]>([]);
  const [regularsInitialized, setRegularsInitialized] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    trackEvent({ name: ANALYTICS_EVENTS.BOOKING_COMPLETE });
  }, [trackEvent]);

  // CRITICAL guard: never display the confirmation surface unless a real
  // reservation was committed. `booking.reservationId` is set inside the
  // commit path (commitReservation.ts → setReservationResult) BEFORE
  // navigation to /booking-final, so a valid landing always has it.
  //
  // Without this guard, /booking-final renders whatever booking state is
  // in sessionStorage — letting users see a "Reserva Confirmada" surface
  // for a reservation that does NOT exist in the backend. Restaurants then
  // receive guests whose reservations they have no record of. (Reported by
  // ops 2026-05-25 after Beast Butchers saw the fake surface.)
  //
  // Reachable when:
  //   - Direct URL access / bookmark / autocomplete to /booking-final
  //   - Browser back-forward to a stale /booking-final history entry
  //   - Any commit path that navigates to /booking-final before
  //     reservationId is written to booking state (shouldn't happen with
  //     flushSync but guarded defensively)
  //
  // Skip the redirect while BookingProvider is still hydrating
  // (`isLoading=true`) — sessionStorage read is synchronous and resolves
  // in the same microtask as mount, so this is at most a single-frame
  // skip in practice (same pattern as PaymentPage's hydration gate).
  const guardFiredRef = useRef(false);
  useEffect(() => {
    if (booking.isLoading) return;
    if (booking.reservationId) return;
    if (guardFiredRef.current) return;
    guardFiredRef.current = true;

    // Classify FIRST, then instrument only the actionable case. A benign
    // cold-load / deep-link / stale-bookmark landing (no_active_session) is
    // NOT a fake confirmation — it's someone reopening a dead /booking-final
    // URL. We render the friendly expired-session card below regardless, but
    // we deliberately emit NO telemetry for it: no Sentry, no FullStory.
    //
    // Why: this case ran ~450/day. Tagging those sessions in FullStory with
    // `booking_final_no_reservation_id` (an event name that reads as "fake
    // confirmation") and capturing a replay URL for each created pure noise
    // that buried the genuine signal and misled anyone filtering FullStory.
    //
    // Why this is safe (no real incident hides in the silenced path):
    //  1. A real commit sets reservationId BEFORE navigating here
    //     (commit/shared.ts persistSuccessAndAdvance: setReservationResult →
    //     goToStep('BOOKING_FINAL'); reservationId comes from
    //     assertReservationId which throws if the backend returned none).
    //     This effect only runs when reservationId is null (guard above) —
    //     a state no successful commit produces.
    //  2. The genuine fake-confirmation signature (null reservationId WITH
    //     real flow progress) classifies as `actionable` and still fires
    //     both Sentry + FullStory. The silenced `no_active_session` path is
    //     null reservationId with ZERO flow progress — which runGuards
    //     (commitReservation.ts) rejects before any commit API call, so it
    //     can only be a cold-load/stale-bookmark, never a real attempt.
    // See docs/plans/sentry-bf-systematic-fix.md (Phase 5).
    //
    // NOTE: guardFiredRef latches before this return, so the no-capture
    // decision is final for this mount. Safe because every hasActiveSession
    // field (date/time/wlDates/flow flags) is set before landing here and is
    // not mutated upward while on /booking-final; the only post-mount change
    // (reservationId null→set) short-circuits at the guard above.
    const guard = classifyBookingFinalGuard(booking);
    if (guard.guardSubset === 'no_active_session') {
      return;
    }

    // Actionable fake-confirmation signal. Tag the session in FullStory
    // (filterable by event:booking_final_no_reservation_id, now noise-free)
    // and capture the replay URL so the Sentry event carries a one-click
    // deep-link to the moment the guard fired. Both no-op cleanly if
    // FullStory isn't loaded (dev / preview / blocked / SDK not established).
    markFullStoryEvent('booking_final_no_reservation_id', {
      restaurant: restaurant.subdomainPrefix,
      flow_type: booking.flowType,
      guarantee_type: booking.guaranteeType ?? '<null>',
      has_card_token: !!booking.cardToken,
      guard_subset: guard.guardSubset,
    });
    const fullstoryReplayUrl = getFullStoryReplayUrl();

    Sentry.captureMessage(guard.message, {
      level: guard.level,
      tags: {
        guard: guard.guardTag,
        guard_subset: guard.guardSubset,
        'restaurant.slug': restaurant.subdomainPrefix,
      },
      extra: {
        // Diagnostic snapshot — what state the failing user actually has,
        // so we can tell apart direct-URL access, browser-back-to-stale,
        // and any commit-path regression. PII-safe by construction
        // (booleans, IDs, counts only).
        diagnosticState: {
          diners: booking.diners,
          selectedDate: booking.selectedDate,
          selectedTime: booking.selectedTime,
          service: booking.service,
          guaranteeType: booking.guaranteeType,
          feeDeposit: booking.feeDeposit,
          flowType: booking.flowType,
          // Non-standard flow signals — these drive the actionable-vs-benign
          // classification (WL/early-access/agent never set selectedDate), so
          // a triager can confirm a no_active_session verdict was correct.
          wlDatesCount: booking.wlDates.length,
          isEarlyAccessFlow: booking.isEarlyAccessFlow,
          isAgentReservation: booking.isAgentReservation,
          hasCardToken: !!booking.cardToken,
          startedAt: booking.startedAt,
          ageMs: booking.startedAt ? Date.now() - booking.startedAt : null,
          referrer: typeof document !== 'undefined' ? !!document.referrer : null,
        },
        restaurantId: restaurant.id,
        // Deep-link to FullStory replay anchored at the playhead of the
        // moment this guard fired. Null when FullStory isn't loaded yet.
        fullstoryReplayUrl,
      },
    });
    // No toast + auto-navigate here — Phase 10 (2026-05-26): instead of
    // bouncing the user silently to `/`, render the expired-landing card
    // below. Phase 9 data showed 22.9% of guard events are Sub-Y (direct
    // nav + external referrers from Google/Instagram/Gmail), and a card
    // with an explicit CTA converts better than a 5-second toast that
    // auto-dismisses on a homepage the user didn't ask for. The expired
    // card renders for ALL guard fires (incl. benign no_active_session);
    // only the telemetry above is gated to the actionable subset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking.isLoading, booking.reservationId]);

  // SEO: keep /booking-final out of Google's index. Phase 9 data showed
  // 7+ events arriving from www.google.com — search engines have indexed
  // the post-confirmation page and are sending users to a URL that can
  // never render correctly without an in-memory reservation. The page is
  // post-conversion; legitimate landings come from in-app navigation, not
  // search. robots.txt has a matching Disallow rule (defense in depth).
  // Tag mounts and unmounts with the route so other pages stay indexable.
  //
  // Dedupe pattern: only create + remove a tag if WE inserted it. Protects
  // against (a) StrictMode dev double-mount briefly stacking two tags
  // and (b) an HMR cycle that throws between createElement and append
  // leaving a stale tag in the head. The ownedMeta flag scopes the
  // cleanup to our own insertion — never removes a meta tag we didn't
  // create.
  useEffect(() => {
    const existing = document.head.querySelector('meta[name="robots"]');
    let ownedMeta: HTMLMetaElement | null = null;
    if (!existing) {
      ownedMeta = document.createElement('meta');
      ownedMeta.name = 'robots';
      ownedMeta.content = 'noindex,nofollow';
      document.head.appendChild(ownedMeta);
    }
    return () => {
      if (ownedMeta && ownedMeta.parentNode === document.head) {
        document.head.removeChild(ownedMeta);
      }
    };
  }, []);

  // Initialize available regular companions once from query data
  useEffect(() => {
    if (regularCompanions.length > 0 && !regularsInitialized) {
      setAvailableRegulars(regularCompanions);
      setRegularsInitialized(true);
    }
  }, [regularCompanions, regularsInitialized]);

  const { dateLocale } = useLanguage();

  const isWaitingList = booking.flowType === 'waiting_list';
  const isEarlyAccess = booking.isEarlyAccessFlow;

  const displayCurrency = restaurant.reservationGuaranteeCurrency ?? restaurant.currency ?? 'USD';
  const formattedDate = booking.selectedDate
    ? format(new Date(booking.selectedDate + 'T12:00:00'), 'EEEE, MMMM d, yyyy', { locale: dateLocale })
    : '';

  const userDisplayName = !booking.isAgentReservation ? (authState.name ?? '').trim() : '';

  const hasPreferences = !!(booking.allergies || booking.veggie || booking.kids || booking.pets || booking.accessibility);
  const hasPairings = booking.selectedPairings.length > 0;
  const hasGuarantee = booking.guaranteeType !== 'traditional' && booking.feeDeposit > 0;

  const toleranceText = (isWaitingList ? t('GRAB_CONFIRMATION_WL_TOLERANCE') : t('GRAB_CONFIRMATION_TOLERANCE')).trim();

  // Render-time guard counterpart of the guard useEffect above. While
  // BookingProvider is still hydrating from sessionStorage
  // (`isLoading=true`), defer judgment — same pattern as PaymentPage —
  // so a hard refresh / auth-bounce return doesn't briefly flash the
  // expired-landing card to a valid in-flight user.
  // Placed AFTER all hook calls to respect React's rules-of-hooks.
  if (booking.isLoading) return null;
  if (!booking.reservationId) {
    // Phase 10 landing card. Replaces the prior silent navigate('/'):
    // user gets an explicit explanation + a single CTA back into the
    // booking funnel. Covers Pattern A1 (back-nav from auth.v2),
    // Sub-Y.1 (direct-nav / bookmarked URLs), and Sub-Y.2 (external
    // links from Google / Instagram / Gmail / tenant marketing sites).
    // Sentry capture + FullStory mark already fired in the useEffect.
    return (
      <BookingPageLayout title="" progress={0} showBack={false}>
        <div className="w-full sm:px-6 flex flex-col items-center text-center space-y-6 py-12">
          <Clock className="w-16 h-16 text-foreground/40" strokeWidth={1.5} />
          <h2 className="text-2xl font-display text-foreground">
            {t('BOOKING_FINAL_EXPIRED_TITLE')}
          </h2>
          <p className="text-foreground/60 max-w-md">
            {t('BOOKING_FINAL_NO_RESERVATION_TOAST')}
          </p>
          <Button
            data-testid="booking-final-expired-cta"
            className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl hover:bg-brand-dark shadow-md mt-2"
            onClick={() => navigate('/', { replace: true })}
          >
            {t('NEW_HOME_BOOK')}
          </Button>
        </div>
      </BookingPageLayout>
    );
  }

  const showOccasion = !!restaurant.allowOccasion;
  const showPromoCode = !!restaurant.allowPromoCode;
  const showServiceLanguage = !!restaurant.allowServiceLanguage;
  const showCompanions = !!restaurant.allowCompanions && booking.diners > 1;
  const maxCompanions = Math.max(0, booking.diners - 1);

  const handleSelectRegular = (regular: RegularCompanion) => {
    if (companions.length >= maxCompanions) return;
    setCompanions((prev) => [
      ...prev,
      {
        name: regular.name,
        email: regular.email,
        phone: regular.phone,
        id: regular.id,
        isRegular: true,
        notes: regular.notes,
        treatment: regular.treatment,
        relationship: regular.relationship,
      },
    ]);
    setAvailableRegulars((prev) => prev.filter((r) => r.id !== regular.id));
  };

  const handleCompanionRemoved = (_index: number, companion: CompanionData) => {
    if (companion.isRegular && companion.id) {
      const original = regularCompanions.find((r) => r.id === companion.id);
      if (original) {
        setAvailableRegulars((prev) => [...prev, original]);
      }
    }
  };

  const handleFinish = async () => {
    if (!booking.reservationId) {
      booking.resetBooking();
      navigate('/');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Save note and companions in parallel
      const promises: Promise<void>[] = [
        saveReservationNote(currentLanguage, {
          resId: booking.reservationId,
          note: note.trim(),
          occasion: occasion || undefined,
          languageService: languageService || undefined,
          promoCode: promoCode.trim() || undefined,
        }),
      ];

      // Save companions if any have names
      const validCompanions = companions.filter((c) => c.name.trim());
      if (validCompanions.length > 0) {
        promises.push(
          saveCompanions({
            reservationId: booking.reservationId,
            companions: validCompanions,
          }),
        );
      }

      await Promise.all(promises);

      booking.setUserNote(note.trim());

      toast.success(t('GRAB_CONFIRMATION_SAVED'), {
        icon: <Check className="w-4 h-4" />,
        duration: 2000,
      });

      // Brief delay so the user sees the success toast before navigating away
      await new Promise((resolve) => setTimeout(resolve, 1500));

      booking.resetBooking();
      navigate('/');
    } catch {
      setError(t('GRAB_CONFIRMATION_SAVE_ERROR'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BookingPageLayout
      title=""
      progress={100}
      showBack={false}
    >
      <div className="w-full sm:px-6 flex flex-col items-center space-y-6">
        {/* Success / WL Icon */}
        <div data-testid="booking-success" className="animate-fade-in">
          {isWaitingList || isEarlyAccess ? (
            <Clock className="w-20 h-20 text-amber-500" strokeWidth={1.5} />
          ) : (
            <CheckCircle className="w-20 h-20 text-green-500" strokeWidth={1.5} />
          )}
        </div>

        <h2 className="text-3xl font-display text-center">
          {isWaitingList
            ? t('WAITING_LIST_MODAL_CONFIRMATION_DIALOG_TITLE')
            : isEarlyAccess
              ? t('EARLY_ACCESS_CONFIRMED')
              : t('GRAB_CONFIRMATION_TITLE')}
        </h2>

        {booking.uniqueId && (
          <div data-testid="reservation-id" className="bg-foreground/5 rounded-xl p-4 text-center">
            <p className="text-sm text-foreground/50">{t('GRAB_CONFIRMATION_RESERVATION_ID')}</p>
            <p className="text-2xl font-semibold text-foreground tracking-wider mt-1">
              {booking.uniqueId}
            </p>
          </div>
        )}

        {isWaitingList && booking.wlDates.length > 0 && (
          <div className="bg-foreground/5 rounded-xl p-4 w-full space-y-2">
            <p className="text-sm text-foreground/50">{t('BOOKING_FINAL_REQUESTED_DATES')}</p>
            <div className="flex flex-wrap gap-2">
              {booking.wlDates.map((d) => (
                <span key={d} className="text-sm font-medium text-foreground bg-foreground/10 px-3 py-1 rounded-full">
                  {d}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* The agent branch interpolates `booking.guestEmail` directly into
            the translation string — bare PII in DOM text. Wrap the whole
            paragraph so session-replay tools never capture it. The other
            branches are generic copy; masking them is harmless. */}
        <Private>
          <p className="text-foreground/50 text-sm text-center">
            {isWaitingList
              ? t('BOOKING_FINAL_WAITING_LIST_NOTICE')
              : isEarlyAccess
                ? t('EARLY_ACCESS_REQUESTED_MESSAGE')
                : booking.isAgentReservation && booking.guestEmail
                  ? t('GRAB_CONFIRMATION_AGENT_EMAIL_SENT', { email: booking.guestEmail })
                  : t('GRAB_CONFIRMATION_EMAIL_SENT')}
          </p>
        </Private>

        {/* Reservation Summary */}
        <div className="bg-foreground/5 rounded-xl p-5 space-y-3 w-full">
          <h3 className="font-display text-lg font-semibold text-foreground">
            {t('SUMMARY_YOUR_RESERVATION')}
          </h3>
          {userDisplayName && (
            <Private>
              <SummaryRow label={t('profile:MY_ACCOUNT_LABEL_NAME')} value={userDisplayName} />
            </Private>
          )}
          <SummaryRow label={t('SUMMARY_GUESTS')} value={t('PARTYSIZE_LABEL', { count: booking.diners })} />
          {isWaitingList && booking.wlDates.length > 0 ? (
            <div className="flex justify-between items-start">
              <span className="text-sm text-foreground/50">{t('SUMMARY_DATE')}</span>
              <div className="flex flex-wrap gap-1 justify-end max-w-[60%]">
                {booking.wlDates.map((d) => (
                  <span key={d} className="text-sm font-medium text-foreground bg-foreground/10 px-2 py-0.5 rounded">
                    {format(new Date(d + 'T12:00:00'), 'MMM d', { locale: dateLocale })}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <SummaryRow label={t('SUMMARY_DATE')} value={formattedDate} />
          )}
          {booking.selectedTime && (
            <SummaryRow label={t('SUMMARY_TIME')} value={booking.selectedTime} />
          )}
          {booking.service && (
            <SummaryRow label={t('SUMMARY_SERVICE')} value={t(`FILTER_STRING_${booking.service.toUpperCase()}`)} />
          )}
          {booking.selectedArea && !isWaitingList && restaurant.publicSearchMode === PUBLIC_SEARCH_MODE.AREA && (
            <SummaryRow
              label={t('SUMMARY_AREA')}
              value={toTitleCase(localizeAreaById(booking.selectedArea, restaurant.publicAreas, currentLanguage).name)}
            />
          )}
          {booking.selectedExperience && (
            <SummaryRow
              label={t('SUMMARY_EXPERIENCE')}
              value={
                restaurant.menus?.[currentLanguage]?.find(
                  (m: { id: number; name: string }) => String(m.id) === booking.selectedExperience!.id
                )?.name ?? booking.selectedExperience.title
              }
            />
          )}
        </div>

        {/* Preferences Summary */}
        {hasPreferences && (
          <div className="bg-foreground/5 rounded-xl p-5 space-y-3 w-full">
            <h3 className="font-display text-lg font-semibold text-foreground">
              {t('CONFIRMATION_PREFERENCES')}
            </h3>
            {booking.allergies && (
              <Private>
                <SummaryRow label={t('STRING_MENU_ALLERGIES_TITLE')} value={booking.allergies} />
              </Private>
            )}
            {booking.veggie && (
              <SummaryRow label={t('NEW_MODAL_GUESTS_VEGETARIAN')} value={String(booking.howManyVeggie)} />
            )}
            {booking.kids && (
              <SummaryRow label={t('NEW_MODAL_GUESTS_CHILDREN')} value={String(booking.howManyKids)} />
            )}
            {booking.pets && (
              <SummaryRow label={t('NEW_MODAL_PET')} value={t('STRING_YES')} />
            )}
            {booking.accessibility && (
              <SummaryRow label={t('NEW_MODAL_ACCESSIBILITY_TABLE')} value={t('STRING_YES')} />
            )}
          </div>
        )}

        {/* Pairings Summary */}
        {hasPairings && (
          <div className="bg-foreground/5 rounded-xl p-5 space-y-3 w-full">
            <h3 className="font-display text-lg font-semibold text-foreground">
              {t('CONFIRMATION_PAIRINGS')}
            </h3>
            {booking.selectedPairings.map((p) => {
              const resolvedName = resolvePairingName(
                p.id,
                p.name,
                restaurant.beveragePairings,
                currentLanguage,
              );
              return (
                <SummaryRow
                  key={p.id}
                  label={`${resolvedName} (x${p.quantity})`}
                  value={`${displayCurrency} ${(p.price * p.quantity).toLocaleString()}`}
                />
              );
            })}
          </div>
        )}

        {/* Agent: Guest Info Summary */}
        {booking.isAgentReservation && booking.guestName && (
          <GuestDetailsCard
            name={booking.guestName}
            email={booking.guestEmail}
            phone={booking.guestPhone}
            className="w-full"
          />
        )}

        {/* Guarantee Info */}
        {hasGuarantee && (
          <div className="bg-foreground/5 rounded-xl p-5 space-y-2 w-full">
            <h3 className="font-display text-lg font-semibold text-foreground">
              {t('CONFIRMATION_GUARANTEE')}
            </h3>
            <p className="text-sm text-foreground/70">
              {booking.guaranteeType === 'deposit'
                ? t('CONFIRMATION_DEPOSIT_GUARANTEE', { amount: booking.feeDeposit, currency: displayCurrency })
                : t('CONFIRMATION_NO_SHOW_GUARANTEE', { amount: booking.feeDeposit, currency: displayCurrency })}
            </p>
          </div>
        )}

        {/* Tolerance Notice — rendered via sanitizeHtml because restaurants
            can override GRAB_CONFIRMATION_TOLERANCE in their appTexts with
            HTML markup (e.g. <p class="..."><br/>...). The previous
            `<p>{toleranceText}</p>` plain-text rendering showed the raw
            tags to users (reported 2026-05-25, Beast Butchers). Wrapper is
            <div> not <p> so HTML containing block-level tags doesn't
            produce nested-<p> auto-closing in the browser. */}
        {toleranceText && (
          <div className="flex items-start gap-2 w-full text-sm text-foreground/50">
            <Clock className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} />
            <div
              className="flex-1"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(toleranceText) }}
            />
          </div>
        )}

        {/* Special Requirements / Notes — free-form user input. Users
            commonly include names, allergies, anniversaries, kids' info.
            Wrap so session-replay tools don't capture this high-entropy
            PII (allergies + dietary info is also health-adjacent). */}
        <Private className="w-full space-y-2">
          <label className="text-sm font-medium text-foreground/70">
            {t('NEW_MODAL_SPECIAL_REQUIREMENTS')}
          </label>
          <textarea
            className="w-full rounded-xl border border-border p-3 text-foreground focus:border-brand focus:outline-none resize-none"
            rows={3}
            placeholder={t('NEW_MODAL_WRITE_A_NOTE_HERE')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Private>

        {/* Companions */}
        {showCompanions && (
          <div className="w-full space-y-3">
            {availableRegulars.length > 0 && (
              <div className={`space-y-1 ${companions.length >= maxCompanions ? 'opacity-50' : ''}`}>
                <label className="text-sm font-medium text-foreground/70">
                  {t('GRAB_CONFIRMATION_ADD_REGULAR_GUEST')}
                </label>
                <p className="text-sm text-foreground/60">
                  {availableRegulars.map((regular, idx) => (
                    <span key={regular.id}>
                      <button
                        type="button"
                        disabled={companions.length >= maxCompanions}
                        onClick={() => handleSelectRegular(regular)}
                        className="underline text-brand hover:text-brand-dark transition-colors disabled:cursor-not-allowed disabled:text-foreground/40"
                      >
                        {regular.name}
                      </button>
                      {idx < availableRegulars.length - 1 && ', '}
                    </span>
                  ))}
                </p>
              </div>
            )}

            <CompanionInputs
              maxCompanions={maxCompanions}
              companions={companions}
              onChange={setCompanions}
              onRemove={handleCompanionRemoved}
            />
          </div>
        )}

        {/* Occasion */}
        {showOccasion && (
          <div className="w-full space-y-2">
            <label className="text-sm font-medium text-foreground/70">
              {t('NEW_MODAL_SPECIAL_OCCASION')}
            </label>
            <div className="flex flex-wrap gap-2">
              {OCCASIONS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  className={`px-4 py-2 rounded-xl border text-sm transition-all duration-200 ${
                    occasion === o.value
                      ? 'bg-brand text-brand-foreground border-brand'
                      : 'bg-card text-foreground/70 border-border hover:border-brand-light'
                  }`}
                  onClick={() => setOccasion(occasion === o.value ? '' : o.value)}
                >
                  {t(o.labelKey)}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Promo Code */}
        {showPromoCode && (
          <div className="w-full space-y-2">
            <label className="text-sm font-medium text-foreground/70">
              {t('PROMOCODE_LABEL')}
            </label>
            <Input
              type="text"
              placeholder={t('PROMOCODE_PLACEHOLDER')}
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
              className="rounded-xl"
            />
          </div>
        )}

        {/* Service Language */}
        {showServiceLanguage && (
          <div className="w-full space-y-2">
            <label className="text-sm font-medium text-foreground/70">
              {t('NEW_MODAL_HOST_LANGUAGE')}
            </label>
            <div className="flex gap-2">
              {SERVICE_LANGUAGES.map((lang) => (
                <button
                  key={lang.value}
                  type="button"
                  className={`px-5 py-2 rounded-xl border text-sm transition-all duration-200 ${
                    languageService === lang.value
                      ? 'bg-brand text-brand-foreground border-brand'
                      : 'bg-card text-foreground/70 border-border hover:border-brand-light'
                  }`}
                  onClick={() => setLanguageService(languageService === lang.value ? '' : lang.value)}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 text-red-700 rounded-xl p-4 text-sm w-full">
            {error}
          </div>
        )}
      </div>

      <div className="w-full sm:px-6 mt-8">
        <Button
          data-testid="finish-btn"
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
          onClick={handleFinish}
          disabled={isSubmitting}
        >
          {isSubmitting ? t('GRAB_CONFIRMATION_SAVING') : t('CONFIRMATION_MESSAGE_RESERVATION_TEXT_10')}
        </Button>
      </div>
    </BookingPageLayout>
  );
};

export default BookingFinalPage;
